import { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "fr_vendor_filter_v1";

const VendorFilterContext = createContext(null);

/**
 * VendorFilterProvider — holds the selected vendor bucket names (source
 * companies: LCV, ProCall, Kershaw, DB Pest, plus "__untagged__" and any
 * others that surface from `/api/vendors`). Persists to localStorage so
 * the admin's "focus" sticks across visits and pages (Jobs Board + Radar
 * + WOs list all read from this same store).
 */
export function VendorFilterProvider({ children }) {
  const [selected, setSelected] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch { return []; }
  });
  const [vendors, setVendors] = useState([]);   // [{name, count, open, closed}]
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(selected)); }
    catch { /* ignore quota errors */ }
  }, [selected]);

  const reload = async (auth) => {
    setLoading(true);
    try {
      const r = await axios.get(`${BASE}/api/vendors`, auth || {});
      setVendors(r.data.vendors || []);
    } catch { /* silent — sidebar auth guard will surface elsewhere */ }
    finally { setLoading(false); }
  };

  const toggle = (name) => {
    setSelected(prev => prev.includes(name)
      ? prev.filter(x => x !== name)
      : [...prev, name]);
  };
  const clear = () => setSelected([]);
  const setOnly = (name) => setSelected([name]);

  // Query-string fragment for /api/jobs and /api/radar/pins
  const asQuery = useMemo(
    () => selected.length ? selected.join(",") : "",
    [selected]
  );

  const value = { selected, vendors, loading, toggle, clear, setOnly, reload, asQuery };
  return <VendorFilterContext.Provider value={value}>{children}</VendorFilterContext.Provider>;
}

export function useVendorFilter() {
  const ctx = useContext(VendorFilterContext);
  if (!ctx) throw new Error("useVendorFilter must be used inside VendorFilterProvider");
  return ctx;
}
